import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { options } from "../queryParamsToOptions";
import { withTranslation } from "../../translation";
import { getLocation } from "../../../state/modules/router/selectors";
import { changeSort } from "../../../state/modules/search/actions";
import { getPageName } from "../../../state/modules/page/selectors";
import classnames from "classnames";
import styles from "./index.css";

const keyPresses = ["Enter", " ", 13, 32];

const handleChange = ({
  changeSort,
  sortedBy,
  translatedLabel,
  close
}) => () => {
  changeSort({ type: sortedBy, name: translatedLabel });
  close();
};

const handleKeyUp = event => {
  if (
    [event?.key, event?.which].some(keypress => keyPresses.includes(keypress))
  ) {
    event.preventDefault();
    close();
  }
};

const selectedOption = (location, pageName) =>
  options(pageName).find(option => option.value === location.query.sort);

const isSelected = (location, pageName, label) =>
  label === selectedOption(location, pageName).label;

export const LargeScreenSort = ({
  location,
  formatTranslation,
  changeSort,
  close,
  pageName
}) => {
  const sortOptions = options(pageName);
  const selectedIdx = sortOptions.findIndex(({ label }) =>
    isSelected(location, pageName, label)
  );
  const [focusedIdx, setFocusedIdx] = useState(
    selectedIdx >= 0 ? selectedIdx : 0
  );
  const radioRefs = useRef([]);

  useEffect(() => {
    if (radioRefs.current[focusedIdx]) {
      radioRefs.current[focusedIdx].focus();
    }
  }, [focusedIdx]);

  const NAV_KEYS = {
    ArrowLeft: -1,
    ArrowUp: -1,
    ArrowRight: 1,
    ArrowDown: 1
  };

  const handleArrowNav = (event, idx) => {
    const move = NAV_KEYS[event.key];
    if (move) {
      setFocusedIdx((idx + move + sortOptions.length) % sortOptions.length);
      event.preventDefault();

      return;
    }
    if (event.key === " " || event.key === "Enter") {
      handleChange({
        changeSort,
        sortedBy: sortOptions[idx].value,
        translatedLabel: formatTranslation(sortOptions[idx].label),
        close
      })();
      event.preventDefault();
    }
  };

  return (
    <ul className={styles.sortList} role="radiogroup" aria-label="Sort by">
      {sortOptions.map(({ value, label }, idx) => {
        const translatedLabel = formatTranslation(label);
        const selected = isSelected(location, pageName, label);
        const isTabbable = focusedIdx === idx;

        return (
          <li
            tabIndex={isTabbable ? 0 : -1}
            id={`item-${label}`}
            key={`sort=${value}`}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
            role="radio"
            aria-checked={selected}
            className={classnames(styles.sortItem, {
              [styles.current]: location.query && location.query.sort === value
            })}
            onKeyDown={e => handleArrowNav(e, idx)}
            onKeyUp={handleKeyUp}
            onClick={() => {
              setFocusedIdx(idx);
              handleChange({
                changeSort,
                sortedBy: sortOptions[idx].value,
                translatedLabel: formatTranslation(sortOptions[idx].label),
                close
              })();
            }}
            ref={el => (radioRefs.current[idx] = el)}
          >
            <label htmlFor={label}>
              {translatedLabel}
              <input
                tabIndex={-1}
                placeholder={null}
                title={label}
                aria-label={label}
                className={styles.srOnlyWrapper}
                type="radio"
                name="sort"
                id={label}
                value={value}
              />
            </label>
          </li>
        );
      })}
    </ul>
  );
};

LargeScreenSort.displayName = "Sort";

LargeScreenSort.propTypes = {
  location: PropTypes.object.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  formatLink: PropTypes.func.isRequired,
  changeSort: PropTypes.func.isRequired,
  pageName: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired
};

/* istanbul ignore next */
function mapStateToProps(state) {
  return {
    location: getLocation(state),
    pageName: getPageName(state)
  };
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      changeSort
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(LargeScreenSort));
